// order.action.js
import { cartConstants, orderConstants } from "./constants";
import swal from "sweetalert";
import axios from "../helpers/axios";
import { normalizeOrderPayload } from "../utils/orderItemMeta";

const getErrorMessage = (error, fallback = "Ошибка") => {
    return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
};

const buildQuery = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

    return query ? `?${query}` : "";
};

const normalizeSort = (sort) => {
    if (!sort || sort === "AllTime") return undefined;
    return sort;
};

const normalizePlainOrderPayload = (payload = {}) => {
    if (!payload || typeof payload !== "object") return payload;

    return normalizeOrderPayload(payload);
};

const normalizeFormDataOrderPayload = (formData) => {
    if (!formData || typeof formData.get !== "function" || typeof formData.set !== "function") {
        return formData;
    }

    const rawPayload = formData.get("payload");

    if (!rawPayload) return formData;

    try {
        const parsed = typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
        const normalized = normalizePlainOrderPayload(parsed);

        formData.set("payload", JSON.stringify(normalized));
    } catch (error) {
        return formData;
    }

    return formData;
};

export const setOrderMode = (mode) => ({
    type: orderConstants.SET_ORDER_MODE,
    payload: mode,
});

export const addOrder = (payload, officeId) => {
    return async (dispatch) => {
        try {
            const normalizedPayload = normalizePlainOrderPayload(payload);

            const body = officeId
                ? {
                    ...normalizedPayload,
                    officeId,
                    scope: "office",
                }
                : {
                    ...normalizedPayload,
                    scope: normalizedPayload?.scope || "personal",
                };

            dispatch({ type: orderConstants.ADD_USER_ORDER_REQUEST });

            const res = await axios.post("/addOrder", body);

            if (res.status === 201) {
                dispatch({ type: cartConstants.RESET_CART });

                swal({
                    title: "Заказ успешно оформлен",
                    icon: "success",
                    content: {
                        element: "div",
                        attributes: {
                            innerHTML: `
                                <p>Оператор свяжется с вами в ближайшее время.</p>
                                <p><a href="/orderPage">Перейти к заказу</a></p>
                            `,
                        },
                    },
                });

                dispatch({
                    type: orderConstants.ADD_USER_ORDER_SUCCESS,
                    payload: res.data,
                });

                return res.data;
            }

            dispatch({
                type: orderConstants.ADD_USER_ORDER_FAILURE,
                payload: res.data,
            });

            return null;
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка оформления заказа");

            dispatch({
                type: orderConstants.ADD_USER_ORDER_FAILURE,
                payload: { error: message },
            });

            return null;
        }
    };
};

export const addOrderWithScreens = (formData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_REQUEST });

            const preparedFormData = normalizeFormDataOrderPayload(formData);

            const res = await axios.post("/addOrderWithScreens", preparedFormData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.status === 201) {
                dispatch({ type: cartConstants.RESET_CART });

                swal({
                    title: "Заказ успешно оформлен",
                    icon: "success",
                    content: {
                        element: "div",
                        attributes: {
                            innerHTML: `
                                <p>Заказ и скрины успешно сохранены.</p>
                                <p><a href="/orderPage">Перейти к заказу</a></p>
                            `,
                        },
                    },
                });

                dispatch({
                    type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_SUCCESS,
                    payload: res.data,
                });

                return res.data;
            }

            dispatch({
                type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_FAILURE,
                payload: res.data,
            });

            return null;
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка оформления заказа");

            dispatch({
                type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_FAILURE,
                payload: { error: message },
            });

            return null;
        }
    };
};

export const getOrders = (sort, officeId, mode = "office") => {
    return async (dispatch) => {
        try {
            const params = {
                sort: normalizeSort(sort),
                office: officeId || undefined,
                scope: mode,
                mode,
            };

            dispatch({ type: orderConstants.GET_USER_ORDER_REQUEST });

            const res = await axios.get(`/getOrders${buildQuery(params)}`);

            if (res.status === 200) {
                const { orders, counts, meta } = res.data;

                dispatch({
                    type: orderConstants.GET_USER_ORDER_SUCCESS,
                    payload: {
                        orders: orders || [],
                        counts: counts || {},
                        meta: meta || {},
                    },
                });

                return orders || [];
            }

            dispatch({
                type: orderConstants.GET_USER_ORDER_FAILURE,
                payload: res.data || { error: "Ошибка загрузки заказов" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: orderConstants.GET_USER_ORDER_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки заказов") },
            });

            return [];
        }
    };
};

export const getOfficeOrders = (sort, officeId) => {
    return async (dispatch) => {
        try {
            const params = {
                sort: normalizeSort(sort),
                office: officeId || undefined,
            };

            dispatch({ type: orderConstants.GET_USER_ORDER_REQUEST });

            const res = await axios.get(`/officeOrders${buildQuery(params)}`);

            if (res.status === 200) {
                const { orders, counts, meta } = res.data;

                dispatch({
                    type: orderConstants.GET_USER_ORDER_SUCCESS,
                    payload: {
                        orders: orders || [],
                        counts: counts || {},
                        meta: meta || {},
                    },
                });

                return orders || [];
            }

            dispatch({
                type: orderConstants.GET_USER_ORDER_FAILURE,
                payload: res.data || { error: "Ошибка загрузки офисных заказов" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: orderConstants.GET_USER_ORDER_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки офисных заказов") },
            });

            return [];
        }
    };
};

export const getOrder = (payload) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.GET_USER_ORDER_DETAILS_REQUEST });

            const res = await axios.post("/getOrder", payload);

            if (res.status === 200) {
                const { order } = res.data;

                dispatch({
                    type: orderConstants.GET_USER_ORDER_DETAILS_SUCCESS,
                    payload: { order },
                });

                return order;
            }

            dispatch({
                type: orderConstants.GET_USER_ORDER_DETAILS_FAILURE,
                payload: res.data || { error: "Ошибка загрузки заказа" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: orderConstants.GET_USER_ORDER_DETAILS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки заказа") },
            });

            return null;
        }
    };
};

export const findAllOrderByOrderNumber = (search, officeId, mode = "office") => {
    return async (dispatch) => {
        try {
            const params = {
                office: officeId || undefined,
                scope: mode,
                mode,
            };

            dispatch({ type: orderConstants.SEARCH_ORDERS_BY_ORDER_NUMBER_REQUEST });

            const res = await axios.get(`/orders/${encodeURIComponent(search)}${buildQuery(params)}`);

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.SEARCH_ORDERS_BY_ORDER_NUMBER_SUCCESS,
                    payload: {
                        orders: res.data?.orders || [],
                        counts: res.data?.counts || {},
                        meta: res.data?.meta || {},
                        message: res.data?.message || "",
                    },
                });

                return res.data?.orders || [];
            }

            dispatch({
                type: orderConstants.SEARCH_ORDERS_BY_ORDER_NUMBER_FAILURE,
                payload: res.data || { error: "Ошибка поиска заказа" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: orderConstants.SEARCH_ORDERS_BY_ORDER_NUMBER_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка поиска заказа") },
            });

            return [];
        }
    };
};

export const updateOrderVisibleDiscountedTotal = (payload, { sort, officeId, mode = "office" } = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.UPDATE_ORDER_BY_ID_VISIBLE_REQUEST });

            const res = await axios.post("/updateOrderVisible", payload);

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.UPDATE_ORDER_BY_ID_VISIBLE_SUCCESS,
                    payload: res.data,
                });

                dispatch(getOrders(sort, officeId, mode));

                return true;
            }

            dispatch({
                type: orderConstants.UPDATE_ORDER_BY_ID_VISIBLE_FAILURE,
                payload: res.data,
            });

            return false;
        } catch (error) {
            dispatch({
                type: orderConstants.UPDATE_ORDER_BY_ID_VISIBLE_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка обновления заказа") },
            });

            return false;
        }
    };
};

export const addOrderScreens = (formData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_REQUEST });

            const preparedFormData = normalizeFormDataOrderPayload(formData);

            const res = await axios.post("/addOrderScreens", preparedFormData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_SUCCESS,
                    payload: res.data,
                });

                return res.data;
            }

            dispatch({
                type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_FAILURE,
                payload: res.data,
            });

            return null;
        } catch (error) {
            dispatch({
                type: orderConstants.ADD_USER_ORDER_WITH_SCREENS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки файлов заказа") },
            });

            return null;
        }
    };
};

export const changeOrderScope = ({ orderId, scope, officeId, sort }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.CHANGE_ORDER_SCOPE_REQUEST });

            const res = await axios.patch(`/order/${orderId}/scope`, {
                scope,
                officeId,
            });

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.CHANGE_ORDER_SCOPE_SUCCESS,
                    payload: res.data,
                });

                dispatch(setOrderMode(scope));
                dispatch(getOrders(sort, officeId, scope));

                return {
                    ok: true,
                    order: res.data?.order,
                    message: res.data?.message,
                };
            }

            dispatch({
                type: orderConstants.CHANGE_ORDER_SCOPE_FAILURE,
                payload: res.data,
            });

            return {
                ok: false,
                error: res.data?.error || "Ошибка смены типа заказа",
            };
        } catch (error) {
            const message = getErrorMessage(error, "Ошибка смены типа заказа");

            dispatch({
                type: orderConstants.CHANGE_ORDER_SCOPE_FAILURE,
                payload: { error: message },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const requestOrderAccess = ({ orderId, officeId }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.REQUEST_ORDER_ACCESS_REQUEST });

            const res = await axios.post(`/order/${orderId}/access/request`, {
                officeId,
            });

            if (res.status === 200 || res.status === 201) {
                dispatch({
                    type: orderConstants.REQUEST_ORDER_ACCESS_SUCCESS,
                    payload: res.data,
                });

                return true;
            }

            dispatch({
                type: orderConstants.REQUEST_ORDER_ACCESS_FAILURE,
                payload: res.data,
            });

            return false;
        } catch (error) {
            dispatch({
                type: orderConstants.REQUEST_ORDER_ACCESS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка запроса доступа") },
            });

            return false;
        }
    };
};

export const grantOrderAccess = ({ orderId, userId, officeId, sort, mode }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.GRANT_ORDER_ACCESS_REQUEST });

            const res = await axios.post(`/order/${orderId}/access/grant`, {
                userId,
                officeId,
            });

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.GRANT_ORDER_ACCESS_SUCCESS,
                    payload: res.data,
                });

                dispatch(getOrders(sort, officeId, mode));

                return true;
            }

            dispatch({
                type: orderConstants.GRANT_ORDER_ACCESS_FAILURE,
                payload: res.data,
            });

            return false;
        } catch (error) {
            dispatch({
                type: orderConstants.GRANT_ORDER_ACCESS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка выдачи доступа") },
            });

            return false;
        }
    };
};

export const declineOrderAccess = ({ orderId, requestId, officeId, sort, mode }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: orderConstants.DECLINE_ORDER_ACCESS_REQUEST });

            const res = await axios.post(`/order/${orderId}/access/decline`, {
                requestId,
                officeId,
            });

            if (res.status === 200) {
                dispatch({
                    type: orderConstants.DECLINE_ORDER_ACCESS_SUCCESS,
                    payload: res.data,
                });

                dispatch(getOrders(sort, officeId, mode));

                return true;
            }

            dispatch({
                type: orderConstants.DECLINE_ORDER_ACCESS_FAILURE,
                payload: res.data,
            });

            return false;
        } catch (error) {
            dispatch({
                type: orderConstants.DECLINE_ORDER_ACCESS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка отклонения доступа") },
            });

            return false;
        }
    };
};
